import { useMemo } from "react";
import { useSession } from "@/contexts/AuthContext";
import {
  useGetCartItemsQuery,
  useGetWishlistItemsQuery,
} from "@/lib/api/apiSlice";

function toVariantIdMap<T extends { id: string; productVariantId?: string }>(
  items: T[],
): Record<string, string> {
  return items.reduce<Record<string, string>>((acc, item) => {
    if (item.productVariantId) {
      acc[item.productVariantId] = item.id;
    }
    return acc;
  }, {});
}

export function useCartWishlist() {
  const { status } = useSession();
  const skip = status !== "authenticated";

  const { data: cartItems = [] } = useGetCartItemsQuery(undefined, { skip });
  const { data: wishlistItems = [] } = useGetWishlistItemsQuery(undefined, {
    skip,
  });

  const cartMap = useMemo(() => toVariantIdMap(cartItems), [cartItems]);
  const wishlistMap = useMemo(
    () => toVariantIdMap(wishlistItems),
    [wishlistItems],
  );

  return {
    cartItems,
    wishlistItems,
    cartMap,
    wishlistMap,
    cartCount: cartItems.length,
    wishlistCount: wishlistItems.length,
    isInCart: (variantId: string) => Boolean(cartMap[variantId]),
    isWishlisted: (variantId: string) => Boolean(wishlistMap[variantId]),
    getCartItemId: (variantId: string) => cartMap[variantId],
    getWishlistId: (variantId: string) => wishlistMap[variantId],
  };
}
